import { useState, useRef, useEffect } from "react";

export default function Index() {
  return (
    <div className="flex justify-center items-center h-screen">
      <div className="border border-gray-300 p-5 shadow-lg w-1/2">
        <h1 className="text-2xl font-bold mb-5">Post-it Note Canvas with Peel Effect & Edit</h1>
        <CanvasNotes />
      </div>
    </div>
  );
}

function CanvasNotes() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [notes, setNotes] = useState([
    { id: 1, text: "Note 1", x: 50, y: 50, isDragging: false, isEditing: false },
    { id: 2, text: "Note 2", x: 200, y: 100, isDragging: false, isEditing: false },
  ]);
  const [draggingNote, setDraggingNote] = useState<null | number>(null);
  const [editingText, setEditingText] = useState("");

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    for (let i = notes.length - 1; i >= 0; i--) {
      const note = notes[i];
      if (note && x > note.x && x < note.x + 100 && y > note.y && y < note.y + 50) {
        if (!note.isEditing) {
          setDraggingNote(note.id);
          setNotes((prevNotes) =>
            prevNotes.map((n) =>
              n.id === note.id ? { ...n, isDragging: true } : n
            )
          );
        }
        break;
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (draggingNote === null) return;

    const rect = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setNotes((prevNotes) =>
      prevNotes.map((note) =>
        note.id === draggingNote ? { ...note, x: x - 50, y: y - 25 } : note
      )
    );
  };

  const handleMouseUp = () => {
    setDraggingNote(null);
    setNotes((prevNotes) =>
      prevNotes.map((n) =>
        n.isDragging ? { ...n, isDragging: false } : n
      )
    );
  };

  // const handleEditButtonClick = (id: number) => {
  //   const note = notes.find((n) => n.id === id);
  //   if (note) {
  //     setEditingText(note.text);
  //     setNotes((prevNotes) =>
  //       prevNotes.map((n) =>
  //         n.id === id ? { ...n, isEditing: true, isDragging: false } : n
  //       )
  //     );
  //   }
  // };

  const handleSaveEdit = (id: number) => {
    setNotes((prevNotes) =>
      prevNotes.map((n) =>
        n.id === id ? { ...n, text: editingText, isEditing: false } : n
      )
    );
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingText(e.target.value);
  };

  const handleClickToEdit = (id: number) => {
    const note = notes.find((n) => n.id === id);
    if (note) {
      setEditingText(note.text);
      setNotes((prevNotes) =>
        prevNotes.map((n) =>
          n.id === id ? { ...n, isEditing: true } : n
        )
      );
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas!.getContext("2d");

    ctx!.clearRect(0, 0, canvas!.width, canvas!.height);

    notes.forEach((note) => {
      // Apply peeling effect and dragging
      ctx!.save();

      if (note.isDragging) {
        ctx!.shadowColor = "rgba(0, 0, 0, 0.5)";
        ctx!.shadowBlur = 15;
        ctx!.shadowOffsetX = 10;
        ctx!.shadowOffsetY = 10;
        ctx!.translate(note.x + 50, note.y + 25);
        ctx!.rotate(0.15);
        ctx!.scale(1.1, 1.05);
        ctx!.translate(-(note.x + 50), -(note.y + 25));
      } else {
        ctx!.shadowBlur = 0;
      }

      // Draw note background
      ctx!.fillStyle = "#FFD700";
      ctx!.fillRect(note.x, note.y, 100, 50);

      if (!note.isEditing) {
        // Draw note text
        ctx!.fillStyle = "#000";
        ctx!.font = "16px Arial";
        ctx!.fillText(note.text, note.x + 10, note.y + 30);

        // Draw edit button (optional)
        ctx!.fillStyle = "#f00";
        ctx!.font = "12px Arial";
        ctx!.fillText("Edit", note.x + 70, note.y + 45);
      }

      ctx!.restore();
    });
  }, [notes]);

  return (
    <div className="relative">
        <div className="debug" id="debug"></div>
      <canvas
        ref={canvasRef}
        width={400}
        height={300}
        className="border border-gray-400"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onClick={(e) => {
          const rect = canvasRef.current!.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          document.getElementById("debug")!.innerText = `x: ${x}, y: ${y}`;

          // Check if user clicks directly on the note text to edit
          let alreadyEditing = notes.some((note) => note.isEditing);
          if (alreadyEditing) {
            notes.filter(x => x.isEditing).forEach((note) => {
                setNotes((prevNotes) =>
                    prevNotes.map((n) =>
                    n.id === note.id ? { ...n, isEditing: false } : n
                    )
                );
            })
          }

          notes.forEach((note) => {
            if (x > note.x && x < note.x + 100 && y > note.y && y < note.y + 50) {
              handleClickToEdit(note.id);
            }
          });
        }}
      />
      {notes.map((note) =>
        note.isEditing ? (
          <div
            key={note.id}
            className="absolute"
            style={{
              top: `${note.y}px`,
              left: `${note.x}px`,
            }}
          >
            <textarea
              value={editingText}
              onChange={handleTextChange}
              className="w-24 h-16 bg-yellow-300 border border-gray-500 p-2"
            />
            <button
              onClick={() => handleSaveEdit(note.id)}
              className="bg-blue-500 text-white px-2 py-1 mt-1"
            >
              Save
            </button>
          </div>
        ) : null
      )}
    </div>
  );
}
